import type {
  AdItem,
  BloomHandoffRequest,
  BloomHandoffResponse,
  BloomRuntimeConfig,
  ClientType,
  OfferItem,
} from '@apm/contracts';
import type { HandoffOutcome } from './handoff.js';
import { postHandoffJson, requestTv2mTrigger } from './tv2m.js';

/**
 * 구매 핸드오프. **모드가 목적지를 정한다** (`docs/12` §2.4 의 표).
 *
 * ── 왜 federated 는 TV2M 을 직접 부르지 않는가 ────────────────
 *
 * | | federated | delegated |
 * |---|---|---|
 * | 목적지 | `POST /v1/bloom/handoff` (BLOOM Server) | `POST /v1/trigger` (TV2M) |
 * | 인증 | Bearer (선택) | 없음 |
 * | Tier 1 | **성립한다** | 성립하지 않는다 |
 *
 * 계정으로 기기를 찾는 것은 **계정의 주인**인 BLOOM Server 다 (절대 규칙 15). 앱이 TV2M 을 직접
 * 부르면 그 목록을 실어 보낼 방법이 없고, 그러면 IP 가 다른 두 기기 — 회사 PC 와 LTE 폰 —
 * 는 영원히 만나지 못한다. **B9 이전의 시청 화면이 정확히 그 상태였다**: federated 로
 * 띄워도 트리거가 TV2M 으로 직행해 Tier 1 이 한 번도 발화하지 않았다.
 *
 * 파트너 키가 브라우저에 내려가지 않는 것도 같은 구조의 결과다. 그 키는 서버만 알고
 * (`/v1/match*` 의 XFF 신뢰 근거, 절대 규칙 18) BLOOM Server 가 대신 말한다.
 *
 * ── 요청 본문은 한 벌이다 ───────────────────────────────────
 *
 * `BloomHandoffRequest` 의 모든 필드가 `TriggerRequest` 에도 있다. **모드는 배포 설정이지
 * 서로 다른 코드가 아니므로**(절대 규칙 17) 화면은 본문을 한 번만 조립하고, 갈리는 것은
 * 주소와 헤더뿐이다. 응답도 `HandoffOutcome` 한 모양으로 모인다.
 */
export async function requestPurchaseHandoff(
  runtime: BloomRuntimeConfig,
  request: BloomHandoffRequest,
  options: { bloomServerUrl: string; token: string | null },
): Promise<HandoffOutcome> {
  if (runtime.mode === 'delegated') {
    /*
     * 위탁형은 v7 경로 그대로다 — 무인증 `/v1/trigger`. **지우지 말 것**: 계정을
     * 요구하지 않는 사업자 정책과 CTV 광고 트리거가 이 경로 위에 선다 (`docs/12` §2.2).
     */
    return await requestTv2mTrigger(runtime.tv2mServerUrl, request);
  }

  const base = options.bloomServerUrl.replace(/\/+$/, '');
  return await postHandoffJson<BloomHandoffResponse>(`${base}/v1/bloom/handoff`, request, {
    /*
     * **토큰이 Tier 1 의 입력이다.** 서버는 이것으로만 계정을 알고(`OptionalBloomAuthGuard`),
     * 없으면 `accountDeviceIds` 를 아예 싣지 않아 가구(Tier 2)로 내려간다. 익명 시청이
     * 막히는 것이 아니라 **계정 매칭만 성립하지 않는다.**
     *
     * **토큰이 있으면 가구로 내려가지 않는다** — 계정에 묶인 폰이 0대여도 곧바로 QR 이다
     * (`docs/21` §2). 그 구분을 만드는 것이 이 헤더 한 줄이다.
     */
    ...(options.token === null ? {} : { headers: { Authorization: `Bearer ${options.token}` } }),
  });
}

/**
 * ★ **광고 핸드오프의 본문** (v21, `docs/45` §7.5). **순수 함수다** — 봉투가 그대로
 * 실리는지를 node 테스트가 본다.
 *
 * **`campaignId` 를 조립하지 않는다.** 광고의 그것은 `Creative.id` 그대로이고, 폰이 그
 * 값으로 `GET /v1/creatives/{campaignId}` 를 부른다 — `${channelId}:${creativeId}` 로
 * 만들면 **404** 다 (`docs/45` §14 의 20).
 *
 * **`ad` 는 VAST 가 준 봉투 그대로다.** BLOOM 은 그것을 해석하지 않고 옮겨 담기만 하고
 * (절대 규칙 53), TV2M 이 사본을 남겨 폰에 돌려준다. **아무도 광고 서버를 두 번 부르지
 * 않는다** — 그것이 상용 교체가 성립하는 조건이다.
 */
export function buildAdHandoffRequest(input: {
  clientType: ClientType;
  ad: AdItem;
  /** 광고 중에도 재생 맥락은 그 회차다 — 상관관계 키로 싣는다 */
  contentProductId: string | null;
  channelId: string | null;
  playlistId: string | null;
  receiverDeviceId: string;
  senderDeviceId: string | null;
}): BloomHandoffRequest {
  return {
    clientType: input.clientType,
    triggerType: 'ad',
    // **조립하지 않는다.** 위 ★ 참조.
    campaignId: input.ad.creativeId,
    productId: input.ad.creativeId,
    ...(input.channelId === null ? {} : { channelId: input.channelId }),
    // 지금 무엇을 보다가 눌렀는가. 광고는 `Product` 가 아니므로 여기가 그 자리다.
    ...(input.contentProductId === null ? {} : { contentId: input.contentProductId }),
    ...(input.playlistId === null ? {} : { playlistId: input.playlistId }),
    receiverDeviceId: input.receiverDeviceId,
    ...(input.senderDeviceId === null ? {} : { senderDeviceId: input.senderDeviceId }),
    // **VAST 가 준 것 그대로.** 여기서 값을 만들지 않는다.
    ad: input.ad.handoff,
  };
}

/**
 * ★ **상품 핸드오프의 본문** (v23, `docs/47` §9.3). **순수 함수다** — 봉투가 그대로
 * 실리는지를 node 테스트가 본다.
 *
 * ── `buildAdHandoffRequest` 와 나란히 두고 읽을 것 ──────────
 *
 * **합치지 않는다** (`docs/47` §9.3). 하나로 만들면 `triggerType` 별 분기가 그 함수 안에
 * 셋 생기고, **그것이 정확히 「하나로 합치면 한쪽만 고쳐진다」의 자리**다.
 *
 * ── ★ `campaignId` 를 조립하지 않는다 (절대 규칙 62) ─────────
 *
 * 상품의 그것은 **브로커가 만든 `offerId` 그대로**이고, 폰이 그 값으로
 * `GET /v1/creatives/{campaignId}` 를 불러 사본을 읽는다 — `${channelId}:${offerId}` 로
 * 만들면 **404** 다 (v21 §14 의 20 이 광고에서 밟은 그것).
 *
 * **`campaignIdOf(channelId, productId)` 를 이 경로에 쓰지 말 것** — 그 함수는
 * `channel_content` 의 것이다.
 *
 * ── ★ 봉투를 골라 담는다. 통째로 싣지 않는다 ────────────────
 *
 * `OfferItem` 에는 `partnerName`·`matchedCategoryId`·`tracking` 처럼 **봉투에 없는
 * 필드**가 있고, 통째로 실으면 `forbidNonWhitelisted` 가 **요청 전체를 400** 으로
 * 만든다 — 그리고 **화면에는 증상이 없다**(v9 함정 ②).
 *
 * **필드 이름 셋이 낡았고 그것을 아는 채로 둔다** — 대응을 만드는 자리가 여기 하나다
 * (`docs/47` §3.5 의 표).
 *
 * | 봉투의 이름 | 상품에서는 무엇인가 |
 * |---|---|
 * | `productId`    | `OfferItem.offerId` |
 * | `advertiserId` | `OfferItem.partnerId` — 광고주가 아니라 **제휴사**다 |
 * | `landingUrl`   | **`OfferItem.deepLink`** — 「웹 랜딩」이 아니라 **「폰이 열 곳」**이다 |
 *
 * **셋째가 헷갈리기 쉽다.** `guardItem` 이 딥링크가 없을 때 이미 웹 랜딩으로 채워 두므로
 * (그리고 `handoffSource` 에 그 사실을 남기므로) 여기서는 **`deepLink` 를 그대로 쓴다** —
 * 여기서 다시 고르면 **강등 판정이 두 곳에 생긴다.**
 */
export function buildOfferHandoffRequest(input: {
  clientType: ClientType;
  offer: OfferItem;
  /** 상품 카드 중에도 재생 맥락은 그 회차다 — 상관관계 키로 싣는다 */
  contentProductId: string | null;
  channelId: string | null;
  playlistId: string | null;
  receiverDeviceId: string;
  senderDeviceId: string | null;
}): BloomHandoffRequest {
  return {
    clientType: input.clientType,
    triggerType: 'content_offer',
    // **조립하지 않는다.** 위 ★ 참조.
    campaignId: input.offer.offerId,
    productId: input.offer.offerId,
    ...(input.channelId === null ? {} : { channelId: input.channelId }),
    // 지금 무엇을 보다가 눌렀는가. 상품은 `Product` 가 아니므로 여기가 그 자리다.
    ...(input.contentProductId === null ? {} : { contentId: input.contentProductId }),
    ...(input.playlistId === null ? {} : { playlistId: input.playlistId }),
    receiverDeviceId: input.receiverDeviceId,
    ...(input.senderDeviceId === null ? {} : { senderDeviceId: input.senderDeviceId }),
    // **브로커가 준 것에서 골라 담는다.** 여기서 값을 만들지 않는다.
    offer: {
      productId: input.offer.offerId,
      title: input.offer.title,
      price: input.offer.price,
      thumbnailUrl: input.offer.thumbnailUrl,
      // **제휴사다. 광고주가 아니다.**
      advertiserId: input.offer.partnerId,
      couponCode: input.offer.couponCode,
      handoffChannelId: null,
      // **「폰이 열 곳」이다** — `guardItem` 이 강등까지 이미 끝냈다.
      landingUrl: input.offer.deepLink,
    },
  };
}
